import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CurveChart from './CurveChart';
import { lockDraw } from './curveApi';
import { resolveLabel, fmtDate, fmtTick, fmtV, addDaysStr, useCurveCeremony } from './curveFormat';
import { useReveal } from '../../shell/motion';

const SLEEPERCDN = 'https://sleepercdn.com/content/nfl/players/thumb/';

/**
 * CurveDeskPanel — today's Curve, playable in place on Home.
 * Home owns the /today fetch and passes it down as `data`; locking reports
 * the server result back up through onLocked so Home can refresh the tape.
 * Styles live in shell.css under .ft-cvdesk.
 */
const CurveDeskPanel = ({ data, tape, streak = 0, user, onLocked, onResync }) => {
  const navigate = useNavigate();
  const [drawn, setDrawn] = useState(null);
  const [locking, setLocking] = useState(false);
  const [justLocked, setJustLocked] = useState(false); // button settle beat before the lock commits
  const [chartKey, setChartKey] = useState(0); // bump to clear the chart on redraw
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange));
  }, []);

  // Next-morning ceremony — the most recently settled draw, shown once.
  const ceremony = useCurveCeremony(tape);
  const ceremonyShown = useReveal(!!ceremony, 20);

  if (!data) {
    return (
      <div className="ft-cvdesk">
        <span className="ck">The Curve · Today</span>
        <p className="cap" style={{ padding: '28px 0 36px' }}>Warming up the griddle…</p>
      </div>
    );
  }

  const { line, myDraw, band } = data;

  if (!line) {
    return (
      <div className="ft-cvdesk">
        <span className="ck">The Curve · Today</span>
        <div style={{ padding: '22px 0 30px' }}>
          <div className="nm">No curve today.</div>
          <p className="cap" style={{ marginTop: 6 }}>A new one drops at midnight ET.</p>
        </div>
      </div>
    );
  }

  const locked = !!myDraw;
  const settleLabel = resolveLabel(line.resolveDate);

  const handleLock = async () => {
    if (!drawn || locking) return;
    setLocking(true);
    try {
      const result = await lockDraw(drawn);
      if (navigator.vibrate) navigator.vibrate(20);
      // Button settles into "Locked" first; the real commit (which flips
      // `locked` and swaps in the chart's band) lands a beat later.
      setDrawn(null);
      setJustLocked(true);
      setTimeout(() => {
        setJustLocked(false);
        if (onLocked) onLocked(result);
      }, 280);
    } catch (e) {
      if (e.status === 409 && onResync) onResync(); // locked elsewhere — resync
    } finally {
      setLocking(false);
    }
  };

  const cap = locked || justLocked
    ? user
      ? 'Locked in — KTC grades it every morning.'
      : 'Locked in as a guest — log in to keep the streak.'
    : drawn
      ? 'Now lock it in — one line a day, locking is final.'
      : `Where does he go by ${settleLabel} — draw it on the chart.`;

  const ticks = (() => {
    const t = [];
    const hist = line.history || [];
    if (hist.length > 2) {
      t.push({ kind: 'history', index: 0, label: fmtTick(hist[0].date) });
      const mid = Math.floor(hist.length / 2);
      t.push({ kind: 'history', index: mid, label: fmtTick(hist[mid].date) });
    }
    if (line.horizonWeeks >= 4 && line.startDate) {
      const midWeek = Math.floor(line.horizonWeeks / 2);
      const midDate = addDaysStr(line.startDate, midWeek * 7);
      const wk = resolveLabel(midDate);
      t.push({ kind: 'week', week: midWeek, label: wk.startsWith('Week') ? wk.toUpperCase() : fmtTick(midDate) });
    }
    return t;
  })();

  return (
    <div className="ft-cvdesk">
      {ceremony ? (
        <div className={`ft-cvdesk-ceremony ft-ceremony${ceremonyShown ? ' show' : ''}`}>
          <span className="k">{ceremony.kicker}</span>
          <p>{ceremony.sentence}</p>
        </div>
      ) : null}

      <span className="ck">The Curve · Today</span>

      <div className="ph">
        <span className="ava">
          {line.player.sleeperId ? (
            <img
              src={`${SLEEPERCDN}${line.player.sleeperId}.jpg`}
              alt=""
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : null}
          {(line.player.name || '?').slice(0, 2).toUpperCase()}
        </span>
        <span style={{ minWidth: 0 }}>
          <span className="nm" style={{ display: 'block' }}>{line.player.name}</span>
          <span className="ps" style={{ display: 'block' }}>
            {[line.player.position, line.player.team].filter(Boolean).join(' · ')}
          </span>
        </span>
        <span className="val">
          <em>TODAY</em>
          <b>{fmtV(line.startValue)}</b>
        </span>
      </div>

      <CurveChart
        key={chartKey}
        history={line.history}
        startValue={line.startValue}
        horizonWeeks={line.horizonWeeks}
        band={locked ? band : null}
        myPoints={locked ? myDraw.points : null}
        interactive={!locked}
        onDrawChange={setDrawn}
        tall={isMobile}
        endLabel={fmtDate(line.resolveDate)}
        endWeekLabel={settleLabel.startsWith('Week') ? settleLabel.toUpperCase() : ''}
        ticks={ticks}
      />

      <div className="foot">
        {streak > 0 ? <span className="ft-chip cream">{streak}-day streak</span> : null}
        <span className="cap">{cap}</span>
        {locked ? (
          <button type="button" className="ft-btn sm" onClick={() => navigate('/curve')}>
            See the tape
          </button>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flex: 'none' }}>
            {drawn && !justLocked ? (
              <button
                type="button"
                className="redraw"
                onClick={() => { setDrawn(null); setChartKey((k) => k + 1); }}
              >
                ↺ Redraw
              </button>
            ) : null}
            <button
              type="button"
              className={`ft-btn sm${justLocked ? ' is-locked' : ''}`}
              disabled={!drawn || locking || justLocked}
              onClick={handleLock}
            >
              {justLocked ? 'Locked' : locking ? 'Locking…' : 'Lock it in'}
            </button>
          </span>
        )}
      </div>
    </div>
  );
};

export default CurveDeskPanel;
